import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { UserAvatar } from '@/components/UserAvatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Trophy, Crown, Medal, Loader2, Settings2, UserPlus, ShieldCheck, Info, Save,
} from 'lucide-react';
import { metricIcon, unitForMetric, fmtNum, ACCENT_FALLBACK } from '@/lib/gamify';

const METRICS = ['sessions', 'time_on_water', 'distance', 'streak'];
const METRIC_ACCENT = {
  sessions: '#00B4D8',
  time_on_water: '#2EE59D',
  distance: '#48CAE4',
  streak: '#FF6B35',
};

const RankBadge = ({ rank }) => {
  if (rank === 1) return <span className="w-8 h-8 rounded-full bg-[#FFB020]/15 text-[#FFB020] flex items-center justify-center shrink-0"><Crown className="w-4 h-4" /></span>;
  if (rank === 2) return <span className="w-8 h-8 rounded-full bg-white/10 text-white/80 flex items-center justify-center shrink-0"><Medal className="w-4 h-4" /></span>;
  if (rank === 3) return <span className="w-8 h-8 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] flex items-center justify-center shrink-0"><Medal className="w-4 h-4" /></span>;
  return <span className="w-8 h-8 rounded-full bg-white/5 text-muted-foreground font-data text-sm flex items-center justify-center shrink-0">{rank}</span>;
};

const SettingsDialog = ({ open, onOpenChange, initialOptIn, initialName, onSaved }) => {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [optIn, setOptIn] = useState(initialOptIn);
  const [name, setName] = useState(initialName || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setOptIn(initialOptIn); setName(initialName || ''); }
  }, [open, initialOptIn, initialName]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/leaderboard/settings', {
        opt_in: optIn,
        display_name: name.trim() || null,
      });
      await refreshUser();
      toast.success(t('leaderboard.saved'));
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, t('leaderboard.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#12161B] border-white/15 text-white max-w-md" data-testid="leaderboard-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5 text-[#48CAE4]" /> {t('leaderboard.settingsTitle')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">{t('leaderboard.settingsDesc')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="flex items-center justify-between gap-4 rounded-xl bg-[#0A0A0A] border border-white/10 p-3.5">
            <div className="min-w-0">
              <div className="font-medium text-sm">{t('leaderboard.optInLabel')}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t('leaderboard.optInHint')}</div>
            </div>
            <Switch checked={optIn} onCheckedChange={setOptIn} data-testid="leaderboard-optin-switch" />
          </div>

          <div>
            <Label htmlFor="lb-name">{t('leaderboard.displayNameLabel')}</Label>
            <Input
              id="lb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={30}
              placeholder={t('leaderboard.displayNamePlaceholder')}
              disabled={!optIn}
              className="mt-1.5 bg-[#0A0A0A] border-white/15"
              data-testid="leaderboard-name-input"
            />
            <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {t('leaderboard.displayNameHint')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/15 bg-white/5 hover:bg-white/10">{t('common.cancel', 'Annuleren')}</Button>
          <Button onClick={save} disabled={saving} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold" data-testid="leaderboard-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> {t('leaderboard.save')}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Leaderboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [metric, setMetric] = useState('sessions');
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({ opt_in: false, display_name: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/leaderboard', { params: { metric, period } });
      setData(data);
    } catch (e) {
      toast.error(apiError(e, t('leaderboard.loadError')));
    } finally {
      setLoading(false);
    }
  }, [metric, period, t]);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/leaderboard/settings');
      setSettings(data);
    } catch (e) {
      // Non-blocking: keep default (opted-out) settings on failure.
      console.warn('[Leaderboard] failed to load settings', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const accent = METRIC_ACCENT[metric] || ACCENT_FALLBACK;
  const unit = t(`gamify.units.${unitForMetric(metric)}`);
  const MetricIcon = metricIcon(metric);
  const rows = data?.leaderboard || [];
  const optedIn = settings.opt_in;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-8" data-testid="leaderboard-page">
      <div className="label-eyebrow text-[#48CAE4] mb-2">{t('leaderboard.eyebrow')}</div>
      <h1 className="font-display text-4xl sm:text-5xl flex items-center gap-3">{t('leaderboard.title')} <Trophy className="w-8 h-8 text-[#FFB020]" /></h1>
      <p className="text-muted-foreground mt-1 max-w-2xl">{t('leaderboard.subtitle')}</p>

      {/* Opt-in / status card */}
      <div className="kite-card p-5 mt-6 flex items-center justify-between gap-4 flex-wrap" data-testid="leaderboard-status-card">
        {optedIn ? (
          <>
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 rounded-xl bg-[#2EE59D]/12 flex items-center justify-center shrink-0"><ShieldCheck className="w-5 h-5 text-[#2EE59D]" /></span>
              <div className="min-w-0">
                <div className="font-semibold truncate" data-testid="leaderboard-my-name">{settings.display_name || user?.name}</div>
                <div className="text-xs text-muted-foreground">
                  {data?.my_rank
                    ? t('leaderboard.youAreRank', { rank: data.my_rank, total: data.total })
                    : t('leaderboard.notRankedYet')}
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={() => setSettingsOpen(true)} className="border-white/15 bg-white/5 hover:bg-white/10 rounded-xl" data-testid="leaderboard-edit-btn">
              <Settings2 className="w-4 h-4 mr-2" /> {t('leaderboard.editBtn')}
            </Button>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-10 h-10 rounded-xl bg-[#00B4D8]/12 flex items-center justify-center shrink-0"><UserPlus className="w-5 h-5 text-[#00B4D8]" /></span>
              <div className="min-w-0">
                <div className="font-semibold">{t('leaderboard.optInTitle')}</div>
                <div className="text-xs text-muted-foreground">{t('leaderboard.optInDesc')}</div>
              </div>
            </div>
            <Button onClick={() => setSettingsOpen(true)} className="bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="leaderboard-join-btn">
              <UserPlus className="w-4 h-4 mr-2" /> {t('leaderboard.joinBtn')}
            </Button>
          </>
        )}
      </div>

      {/* Period toggle */}
      <div className="flex items-center gap-2 mt-6" data-testid="leaderboard-period-toggle">
        {['all', 'month'].map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
              period === p
                ? 'bg-[#00B4D8]/15 border-[#00B4D8]/40 text-[#48CAE4]'
                : 'bg-white/[0.03] border-white/8 text-muted-foreground hover:text-white'
            }`}
            data-testid={`leaderboard-period-${p}`}
          >
            {t(`leaderboard.periods.${p}`)}
          </button>
        ))}
      </div>

      {/* Metric tabs */}
      <Tabs value={metric} onValueChange={setMetric} className="mt-4">
        <TabsList className="bg-[#12161B] border border-white/10 p-1 h-auto flex-wrap" data-testid="leaderboard-metric-tabs">
          {METRICS.map((m) => (
            <TabsTrigger
              key={m}
              value={m}
              className="data-[state=active]:bg-[#00B4D8] data-[state=active]:text-black text-muted-foreground rounded-lg px-3 py-1.5 text-sm"
              data-testid={`leaderboard-metric-${m}`}
            >
              {t(`leaderboard.metrics.${m}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Board */}
      <div className="kite-card p-4 sm:p-5 mt-4" data-testid="leaderboard-board">
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="w-7 h-7 text-[#00B4D8] animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center" data-testid="leaderboard-empty">
            <div className="w-14 h-14 rounded-2xl bg-[#FFB020]/12 flex items-center justify-center mx-auto"><Trophy className="w-7 h-7 text-[#FFB020]" /></div>
            <p className="font-medium mt-4">{t('leaderboard.empty')}</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">{t('leaderboard.emptyDesc')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.user_id}
                className={`flex items-center gap-3 p-2.5 rounded-xl border ${
                  r.is_me ? 'bg-[#00B4D8]/10 border-[#00B4D8]/30' : 'bg-white/[0.03] border-white/5'
                }`}
                data-testid={`leaderboard-row-${r.rank}`}
              >
                <RankBadge rank={r.rank} />
                <UserAvatar name={r.name} size={34} />
                <span className="flex-1 truncate text-sm font-medium">
                  {r.name}
                  {r.is_me && <span className="text-[#48CAE4]"> · {t('leaderboard.you')}</span>}
                </span>
                <span className="font-data text-sm font-semibold flex items-center gap-1.5" style={{ color: accent }}>
                  <MetricIcon className="w-3.5 h-3.5" /> {fmtNum(r.value)} <span className="text-muted-foreground font-normal">{unit}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4 flex items-start gap-1.5 max-w-2xl">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {t('leaderboard.privacyNote')}
      </p>

      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        initialOptIn={settings.opt_in}
        initialName={settings.display_name}
        onSaved={(s) => { setSettings(s); load(); }}
      />
    </div>
  );
}
